import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

const buildNormalizedError = (error) => ({
  status: error?.response?.status || null,
  message: error?.response?.data?.message || error?.message || "request failed",
  errorCode: error?.response?.data?.errorCode || null,
  existingRatingId: error?.response?.data?.existingRatingId || null,
  raw: error,
});

const throwNormalizedError = (error) => {
  throw buildNormalizedError(error);
};

export const getRatingByConversation = async (conversationId) => {
  try {
    const response = await axiosInstance.get(
      ApiEndPoints.RATINGS.getByConversation(conversationId)
    );
    return response?.data?.data?.rating || null;
  } catch (error) {
    throwNormalizedError(error);
  }
};

export const submitRating = async (payload) => {
  try {
    const response = await axiosInstance.post(ApiEndPoints.RATINGS.submit, payload);
    return response?.data?.data?.rating || null;
  } catch (error) {
    throwNormalizedError(error);
  }
};

export const updateRating = async (ratingId, payload) => {
  try {
    const response = await axiosInstance.patch(
      ApiEndPoints.RATINGS.update(ratingId),
      payload
    );
    return response?.data?.data?.rating || null;
  } catch (error) {
    throwNormalizedError(error);
  }
};

const getRatingCategory = (contentType) => {
  if (contentType.startsWith("image/")) return "rating-image";
  if (contentType.startsWith("video/")) return "rating-video";
  if (contentType.startsWith("audio/")) return "rating-audio";
  return "ratings"; // fallback, though possibly invalid
};

export const createRatingMediaPresignedUrl = async ({ fileName, contentType }) => {
  try {
    const category = getRatingCategory(contentType);
    const response = await axiosInstance.post(ApiEndPoints.UPLOAD.presignedUrl, {
      category,
      fileName,
      contentType,
    });
    return response?.data?.data || null;
  } catch (error) {
    throwNormalizedError(error);
  }
};

export const confirmRatingUpload = async ({ fileKey, category }) => {
  try {
    const response = await axiosInstance.post(ApiEndPoints.UPLOAD.confirm, {
      fileKey,
      category,
    });
    return response?.data?.data || null;
  } catch (error) {
    throwNormalizedError(error);
  }
};

export const uploadRatingFileToS3 = async (uploadUrl, file, contentType) => {
  const parsedUrl = new URL(uploadUrl);
  const urlContentType = parsedUrl.searchParams.get("Content-Type");
  const uploadContentType = urlContentType
    ? decodeURIComponent(urlContentType)
    : contentType || file?.type || "application/octet-stream";

  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": uploadContentType,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error("failed to upload rating media");
  }

  return true;
};

export const uploadRatingMediaFiles = async (files = []) => {
  if (!Array.isArray(files) || files.length === 0) return [];
  const uploadedUrls = [];
  for (const file of files) {
    const fileName = file?.name || `rating-${Date.now()}`;
    const contentType = file?.type || "application/octet-stream";
    const category = getRatingCategory(contentType);

    // Keep sequential order and straightforward error behavior.
    // eslint-disable-next-line no-await-in-loop
    const presigned = await createRatingMediaPresignedUrl({ fileName, contentType });
    if (!presigned?.uploadUrl || !presigned?.fileKey) {
      throw new Error("failed to generate media upload url");
    }
    // eslint-disable-next-line no-await-in-loop
    await uploadRatingFileToS3(presigned.uploadUrl, file, contentType);
    // eslint-disable-next-line no-await-in-loop
    const confirmed = await confirmRatingUpload({
      fileKey: presigned.fileKey,
      category,
    });
    uploadedUrls.push(confirmed?.fileUrl || presigned?.fileUrl);
  }
  return uploadedUrls;
};

export const normalizeRatingError = buildNormalizedError;
